import { ReactNode, useMemo, useState } from 'react'

import { FormControl } from '@/components/lemmala-design-system/form-control/form-control'
import { Modal } from '@/components/lemmala-design-system/modal/modal'
import { Select } from '@/components/lemmala-design-system/select/select'
import { Textarea } from '@/components/lemmala-design-system/textarea/textarea'

import { KEEP_AS_IS, LABELS } from './consts'

export type BulkEditFieldOption = {
  value: string
  label: ReactNode
  // Plain-text hint used for typeahead when `label` is a ReactNode.
  searchText?: string
}

type BulkEditFieldModalProps = {
  title: string
  subtitle: string
  fieldLabel: string
  // Real values only — the shell prepends the "Keep as is" option itself.
  options: BulkEditFieldOption[]
  isPending: boolean
  onConfirm: (value: string) => void | Promise<void>
  onClose: () => void
  // Optional analytics/telemetry hook; fires only on real (non-sentinel) picks.
  onValueChange?: (value: string) => void
  // Optional controlled comment textarea. Provide both to enable — the shell
  // renders the field with a bold label + "(optional)" suffix.
  comment?: string
  onCommentChange?: (comment: string) => void
  isCommentRequired?: boolean
  // Turn off for fields the user can simply set back, like a status toggle.
  showCantBeUndone?: boolean
  // Scope-specific impact line shown once a real value is picked. Renders in
  // the destructive colour and replaces the generic "can't be undone" note.
  destructiveWarning?: ReactNode
}

const getOptionSearchText = (option: BulkEditFieldOption): string => {
  if (option.searchText) return option.searchText
  if (typeof option.label === 'string') return option.label
  return option.value
}

export const BulkEditFieldModal = ({
  title,
  subtitle,
  fieldLabel,
  options,
  isPending,
  onConfirm,
  onClose,
  onValueChange,
  comment,
  onCommentChange,
  isCommentRequired = false,
  showCantBeUndone = true,
  destructiveWarning,
}: BulkEditFieldModalProps) => {
  const [selected, setSelected] = useState<string>(KEEP_AS_IS)
  const hasSelection = selected !== KEEP_AS_IS
  const showComment = onCommentChange !== undefined
  const isMissingRequiredComment = isCommentRequired && (comment ?? '').trim() === ''
  const canConfirm = hasSelection && !isMissingRequiredComment

  const allOptions = useMemo<BulkEditFieldOption[]>(
    () => [{ value: KEEP_AS_IS, label: LABELS.keepAsIs }, ...options],
    [options],
  )

  const handleChange = (next: string) => {
    setSelected(next)
    if (next !== KEEP_AS_IS) onValueChange?.(next)
  }

  return (
    <Modal
      open
      title={title}
      subtitle={subtitle}
      primaryButtonText={LABELS.primaryButton}
      primaryButtonProps={{ disabled: !canConfirm }}
      confirmLoading={isPending}
      onOk={() => (canConfirm ? onConfirm(selected) : undefined)}
      onCancel={onClose}
      onDismiss={onClose}
    >
      <div className='flex flex-col gap-2'>
        <label className='text-main-foreground-primary text-label-default'>{fieldLabel}</label>
        <Select
          className='w-full'
          showSearch
          value={selected}
          onChange={(next) => handleChange((next as string) ?? KEEP_AS_IS)}
          options={allOptions}
          filterOption={(input, option) => {
            if (!option) return false
            const haystack = getOptionSearchText(option as BulkEditFieldOption).toLowerCase()
            return haystack.includes(input.toLowerCase())
          }}
        />
        {hasSelection && destructiveWarning !== undefined && (
          <p className='text-main-foreground-danger text-body-small'>{destructiveWarning}</p>
        )}
        {hasSelection && destructiveWarning === undefined && showCantBeUndone && (
          <p className='text-main-foreground-secondary text-body-default'>{LABELS.cantBeUndone}</p>
        )}
        {showComment && (
          <div className='mt-2'>
            <FormControl label={LABELS.commentLabel} optional={!isCommentRequired}>
              <Textarea
                placeholder={LABELS.commentPlaceholder}
                value={comment ?? ''}
                onChange={(event) => onCommentChange?.(event.target.value)}
              />
            </FormControl>
          </div>
        )}
      </div>
    </Modal>
  )
}
